import React, { useState, useMemo } from 'react';
import { Student, Teacher, SchoolSettings } from '../types.ts';
import { getThemeById } from '../src/themeConfig.ts';
import { RombelGuidanceManager } from '../src/components/RombelGuidanceManager.tsx';
import * as XLSX from 'xlsx';
import { 
  Users, 
  UserPlus, 
  Search, 
  Trash2, 
  Edit3, 
  Check, 
  X, 
  Plus, 
  Download, 
  Printer, 
  AlertCircle,
  UserCheck,
  User,
  Activity,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  HeartHandshake
} from 'lucide-react';

interface WaliRombelStudent {
  id: string;
  teacherId: string;
  studentId?: string; // empty if added manually
  name: string;
  nis?: string;
  nisn?: string;
  class?: string;
  gender: 'Laki-laki' | 'Perempuan';
  notes?: string;
}

interface WaliRombelViewProps {
  waliRombel: WaliRombelStudent[];
  students: Student[];
  classes: string[];
  activeUser: Teacher;
  schoolSettings: SchoolSettings;
  onSaveWali: (item: WaliRombelStudent, method: 'POST' | 'PUT' | 'DELETE') => Promise<any>;
  appTheme?: string;
}

const WaliRombelView: React.FC<WaliRombelViewProps> = ({
  waliRombel = [],
  students = [],
  classes = [],
  activeUser,
  schoolSettings,
  onSaveWali,
  appTheme = 'wzc-themes',
}) => {
  const currentTheme = useMemo(() => getThemeById(appTheme), [appTheme]);
  const [tabMode, setTabMode] = useState<'list' | 'bimbingan' | 'add_existing' | 'add_manual'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  
  // States for Manual Add Form
  const [manualName, setManualName] = useState('');
  const [manualGender, setManualGender] = useState<'Laki-laki' | 'Perempuan'>('Laki-laki');
  const [manualNis, setManualNis] = useState('');
  const [manualNisn, setManualNisn] = useState('');
  const [manualClass, setManualClass] = useState(classes[0] || '');
  const [manualNotes, setManualNotes] = useState('');
  
  // States for Existing Add Selector
  const [selectedClassFilter, setSelectedClassFilter] = useState(classes[0] || 'SEMUA');
  const [existingSearchQuery, setExistingSearchQuery] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<string>>(new Set());

  // Edit Modal State
  const [editingStudent, setEditingStudent] = useState<WaliRombelStudent | null>(null);
  
  // Filter wali rombel list to only show items belonging to this teacher
  const myWaliRombel = useMemo(() => {
    return waliRombel.filter((item) => item.teacherId === activeUser.id);
  }, [waliRombel, activeUser.id]);

  // Statistics
  const stats = useMemo(() => {
    const total = myWaliRombel.length;
    const laki = myWaliRombel.filter(s => s.gender === 'Laki-laki').length;
    const perempuan = myWaliRombel.filter(s => s.gender === 'Perempuan').length;
    return { total, laki, perempuan };
  }, [myWaliRombel]);

  // Sorting State for Student Table
  type SortField = 'name' | 'nis' | 'class' | 'gender' | 'notes';
  type SortOrder = 'asc' | 'desc';

  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Filtered & Sorted Wali Rombel List
  const filteredMyWaliRombel = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    let result = myWaliRombel;
    if (q) {
      result = myWaliRombel.filter(s => 
        s.name.toLowerCase().includes(q) || 
        (s.nis && s.nis.toLowerCase().includes(q)) || 
        (s.class && s.class.toLowerCase().includes(q))
      );
    }

    return [...result].sort((a, b) => {
      let valA = '';
      let valB = '';

      if (sortField === 'name') {
        valA = (a.name || '').toLowerCase();
        valB = (b.name || '').toLowerCase();
      } else if (sortField === 'nis') {
        valA = (a.nis || a.nisn || '').toLowerCase();
        valB = (b.nis || b.nisn || '').toLowerCase();
      } else if (sortField === 'class') {
        valA = (a.class || '').toLowerCase();
        valB = (b.class || '').toLowerCase();
      } else if (sortField === 'gender') {
        valA = (a.gender || '').toLowerCase();
        valB = (b.gender || '').toLowerCase();
      } else if (sortField === 'notes') {
        valA = (a.notes || '').toLowerCase();
        valB = (b.notes || '').toLowerCase();
      }

      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [myWaliRombel, searchQuery, sortField, sortOrder]);

  const renderSortHeader = (label: string, field: SortField, align: 'left' | 'center' | 'right' = 'left') => {
    const isActive = sortField === field;
    return (
      <button
        type="button"
        onClick={() => handleSort(field)}
        className={`flex items-center gap-1.5 font-black text-[10px] uppercase tracking-widest hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors ${
          isActive ? 'text-indigo-600 dark:text-indigo-400 font-bold' : 'text-slate-400'
        } ${align === 'center' ? 'mx-auto' : align === 'right' ? 'ml-auto' : ''}`}
      >
        <span>{label}</span>
        {isActive ? (
          sortOrder === 'asc' ? <ArrowUp size={13} /> : <ArrowDown size={13} />
        ) : (
          <ArrowUpDown size={12} className="opacity-40 hover:opacity-100" />
        )}
      </button>
    );
  };

  // Existing school students who are NOT in the teacher's current Wali Rombel
  const availableSchoolStudents = useMemo(() => {
    const existingWaliStudentIds = new Set(
      myWaliRombel.map(s => s.studentId).filter(Boolean)
    );
    
    return students.filter(s => {
      // Must not be already in the list
      if (existingWaliStudentIds.has(s.id)) return false;
      
      // Class filter
      if (selectedClassFilter !== 'SEMUA' && s.class !== selectedClassFilter) return false;
      
      // Text search
      if (existingSearchQuery) {
        const q = existingSearchQuery.toLowerCase().trim();
        return s.name.toLowerCase().includes(q) || s.nis.toLowerCase().includes(q);
      }
      
      return true;
    });
  }, [students, myWaliRombel, selectedClassFilter, existingSearchQuery]);

  // Add Manual Student Handler
  const handleAddManual = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualName.trim()) return;

    const newStudent: WaliRombelStudent = {
      id: `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      teacherId: activeUser.id,
      name: manualName.trim(),
      gender: manualGender,
      nis: manualNis.trim() || undefined,
      nisn: manualNisn.trim() || undefined,
      class: manualClass || undefined,
      notes: manualNotes.trim() || undefined
    };

    try {
      await onSaveWali(newStudent, 'POST');
      // Reset Form
      setManualName('');
      setManualGender('Laki-laki');
      setManualNis('');
      setManualNisn('');
      setManualNotes('');
      setTabMode('list');
    } catch (err) {
      console.error('Error saving manual student:', err);
    }
  };

  // Add Selected Existing Students Handler
  const handleAddExisting = async () => {
    if (selectedStudentIds.size === 0) return;

    const promises = Array.from(selectedStudentIds).map(studentId => {
      const student = students.find(s => s.id === studentId);
      if (!student) return Promise.resolve();

      const newStudent: WaliRombelStudent = {
        id: `wali_${student.id}`,
        teacherId: activeUser.id,
        studentId: student.id,
        name: student.name,
        gender: student.gender,
        nis: student.nis,
        class: student.class,
        notes: student.classRole || undefined
      };

      return onSaveWali(newStudent, 'POST');
    });

    try {
      await Promise.all(promises);
      setSelectedStudentIds(new Set());
      setTabMode('list');
    } catch (err) {
      console.error('Error adding existing students:', err);
    }
  };

  // Delete Student Handler
  const handleDeleteStudent = async (studentId: string) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus siswa ini dari daftar Wali Rombel Anda?')) {
      try {
        const itemToDelete = myWaliRombel.find(s => s.id === studentId);
        if (itemToDelete) {
          await onSaveWali(itemToDelete, 'DELETE');
        }
      } catch (err) {
        console.error('Error deleting student:', err);
      }
    }
  };

  // Update Student Handler (From Modal)
  const handleUpdateStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingStudent || !editingStudent.name.trim()) return;

    try {
      await onSaveWali(editingStudent, 'PUT');
      setEditingStudent(null);
    } catch (err) {
      console.error('Error updating student:', err);
    }
  };

  // Checkbox helper for existing students multi-select
  const toggleStudentSelection = (id: string) => {
    setSelectedStudentIds(prev => {
      const updated = new Set(prev);
      if (updated.has(id)) {
        updated.delete(id);
      } else {
        updated.add(id);
      }
      return updated;
    });
  };

  // Select/Deselect All visible available students
  const toggleSelectAllAvailable = () => {
    const allAvailableIds = availableSchoolStudents.map(s => s.id);
    const areAllSelected = allAvailableIds.every(id => selectedStudentIds.has(id));

    setSelectedStudentIds(prev => {
      const updated = new Set(prev);
      if (areAllSelected) {
        allAvailableIds.forEach(id => updated.delete(id));
      } else {
        allAvailableIds.forEach(id => updated.add(id));
      }
      return updated;
    });
  };

  // Export to simple print layout
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const html = `
      <html>
        <head>
          <title>Daftar Siswa Wali Rombel - ${activeUser.name}</title>
          <style>
            body { font-family: sans-serif; padding: 40px; color: #333; }
            h1 { font-size: 20px; margin-bottom: 5px; }
            h2 { font-size: 14px; color: #666; margin-top: 0; margin-bottom: 30px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; font-size: 12px; }
            th { bg-color: #f5f5f5; font-weight: bold; }
            .stats { display: flex; gap: 30px; margin-bottom: 20px; font-size: 13px; font-weight: bold; }
            .footer { margin-top: 50px; text-align: right; font-size: 12px; }
            .signature { margin-top: 60px; display: inline-block; border-top: 1px solid #333; width: 200px; text-align: center; padding-top: 5px; }
          </style>
        </head>
        <body>
          <h1>DAFTAR SISWA ROMBONGAN BELAJAR (WALI ROMBEL)</h1>
          <h2>Wali Kelas: ${activeUser.name}</h2>
          
          <div class="stats">
            <div>Total Siswa: ${stats.total}</div>
            <div>Laki-laki: ${stats.laki}</div>
            <div>Perempuan: ${stats.perempuan}</div>
          </div>

          <table>
            <thead>
              <tr>
                <th style="width: 5%">No</th>
                <th style="width: 25%">Nama Lengkap</th>
                <th style="width: 15%">NIS</th>
                <th style="width: 15%">Kelas</th>
                <th style="width: 15%">Jenis Kelamin</th>
                <th>Keterangan / Catatan</th>
              </tr>
            </thead>
            <tbody>
              ${myWaliRombel.map((s, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td><strong>${s.name}</strong></td>
                  <td>${s.nis || '-'}</td>
                  <td>${s.class || '-'}</td>
                  <td>${s.gender}</td>
                  <td>${s.notes || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="footer">
            <p>Ciamis, ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            <p>Wali Kelas,</p>
            <div class="signature">
              <strong>${activeUser.name}</strong><br/>
              NIP: ${activeUser.nip || '-'}
            </div>
          </div>

          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Export Wali Rombel to Excel
  const handleExportExcel = () => {
    const schoolTitle = schoolSettings.schoolName || 'SMP NEGERI 1';
    
    // Header Data
    const headerData = [
      [schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS'],
      [schoolSettings.departmentName || 'DINAS PENDIDIKAN'],
      [schoolTitle.toUpperCase()],
      [schoolSettings.address || ''],
      [],
      [`DAFTAR SISWA KELOMPOK PERWALIAN (WALI ROMBEL)`],
      [`Wali Rombel: ${activeUser.name} (NIP. ${activeUser.nip || '-'})`],
      [`Tahun Ajaran: ${schoolSettings.academicYear || '2025/2026'}`],
      [],
      ['No', 'Nama Siswa', 'NIS', 'NISN', 'Kelas', 'Jenis Kelamin', 'Jabatan / Catatan']
    ];

    // Student Data
    const studentData = filteredMyWaliRombel.map((s, idx) => [
      idx + 1,
      s.name,
      s.nis || '',
      s.nisn || '',
      s.class || '',
      s.gender,
      s.notes || ''
    ]);

    const ws = XLSX.utils.aoa_to_sheet([...headerData, ...studentData]);
    
    // Column Widths
    ws['!cols'] = [
      { wch: 5 },   // No
      { wch: 25 },  // Nama Siswa
      { wch: 15 },  // NIS
      { wch: 15 },  // NISN
      { wch: 12 },  // Kelas
      { wch: 15 },  // Jenis Kelamin
      { wch: 30 }   // Jabatan / Catatan
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Wali_Rombel");
    XLSX.writeFile(wb, `Wali_Rombel_${activeUser.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950 p-4 sm:p-8 custom-scrollbar">
      {/* Header card with details */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 p-6 sm:p-8 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shadow-inner">
              <Users size={24} />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-black text-slate-800 dark:text-white leading-tight">Wali Rombel</h1>
              <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">
                Wali Kelas: <span className="text-indigo-600 dark:text-indigo-400">{activeUser.name}</span>
              </p>
            </div>
          </div>

          {/* Quick Stats Grid */}
          <div className="flex items-center gap-4 self-stretch md:self-auto overflow-x-auto no-scrollbar">
            <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800 rounded-2xl p-3 px-4 flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold text-xs">
                {stats.total}
              </div>
              <div className="leading-none">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">TOTAL SISWA</p>
                <p className="text-xs font-bold text-slate-700 dark:text-slate-300 mt-0.5">Siswa Perwalian</p>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800 rounded-2xl p-3 px-4 flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-xs">
                {stats.laki}
              </div>
              <div className="leading-none">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">LAKI-LAKI</p>
                <p className="text-xs font-bold text-slate-700 dark:text-slate-300 mt-0.5">Putra</p>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800 rounded-2xl p-3 px-4 flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-pink-50 dark:bg-pink-950 text-pink-600 dark:text-pink-400 flex items-center justify-center font-bold text-xs">
                {stats.perempuan}
              </div>
              <div className="leading-none">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">PEREMPUAN</p>
                <p className="text-xs font-bold text-slate-700 dark:text-slate-300 mt-0.5">Putri</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap border-b border-slate-200 dark:border-slate-800 mb-6 gap-2">
          <button
            onClick={() => setTabMode('list')}
            className={`px-5 py-3 text-xs sm:text-sm font-black uppercase tracking-widest border-b-2 transition-all flex items-center gap-2 ${
              tabMode === 'list' 
                ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400' 
                : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'
            }`}
          >
            <Users size={16} />
            <span>Daftar Roster Siswa</span>
          </button>
          <button
            onClick={() => setTabMode('bimbingan')}
            className={`px-5 py-3 text-xs sm:text-sm font-black uppercase tracking-widest border-b-2 transition-all flex items-center gap-2 ${
              tabMode === 'bimbingan' 
                ? 'border-purple-600 text-purple-600 dark:text-purple-400' 
                : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'
            }`}
          >
            <HeartHandshake size={16} />
            <span>Catatan Bimbingan</span>
          </button>
          <button
            onClick={() => setTabMode('add_existing')}
            className={`px-5 py-3 text-xs sm:text-sm font-black uppercase tracking-widest border-b-2 transition-all flex items-center gap-2 ${
              tabMode === 'add_existing' 
                ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400' 
                : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'
            }`}
          >
            <UserCheck size={16} /> Pilih dari Data Siswa
          </button>
          <button
            onClick={() => setTabMode('add_manual')}
            className={`px-5 py-3 text-xs sm:text-sm font-black uppercase tracking-widest border-b-2 transition-all flex items-center gap-2 ${
              tabMode === 'add_manual' 
                ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400' 
                : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'
            }`}
          >
            <UserPlus size={16} /> Tambah Siswa Mandiri
          </button>
        </div>

        {/* TAB: CATATAN BIMBINGAN WALI ROMBEL */}
        {tabMode === 'bimbingan' && (
          <RombelGuidanceManager
            activeUser={activeUser}
            schoolSettings={schoolSettings}
            waliRombelStudents={myWaliRombel}
            allStudents={students}
            classes={classes}
            appTheme={appTheme}
          />
        )}

        {/* TAB 1: LIST OF HOMEROOM STUDENTS */}
        {tabMode === 'list' && (
          <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 p-6 shadow-sm overflow-hidden">
            {/* Search and Action Row */}
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between mb-6">
              <div className="relative w-full sm:w-80">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Cari nama, NIS, kelas..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-bold text-sm text-slate-800 dark:text-slate-100 transition-all focus:border-indigo-500"
                />
              </div>

              <div className="flex items-center gap-2 self-end sm:self-auto">
                <button
                  onClick={handlePrint}
                  disabled={myWaliRombel.length === 0}
                  className="px-5 py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 font-black rounded-2xl text-xs uppercase tracking-widest flex items-center gap-2 transition-all disabled:opacity-50 cursor-pointer"
                >
                  <Printer size={14} /> Cetak Daftar
                </button>

                <button
                  onClick={handleExportExcel}
                  disabled={myWaliRombel.length === 0}
                  className="px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-2xl text-xs uppercase tracking-widest flex items-center gap-2 transition-all disabled:opacity-50 cursor-pointer"
                >
                  <Download size={14} /> Export Excel
                </button>
              </div>
            </div>

            {/* List Table */}
            {filteredMyWaliRombel.length === 0 ? (
              <div className="text-center py-16 px-4">
                <div className="w-16 h-16 rounded-full bg-slate-50 dark:bg-slate-800/40 flex items-center justify-center mx-auto mb-4 border border-slate-100 dark:border-slate-800">
                  <Users size={28} className="text-slate-400" />
                </div>
                <h3 className="text-lg font-black text-slate-800 dark:text-white mb-1">Rombel Masih Kosong</h3>
                <p className="text-xs text-slate-400 font-semibold max-w-md mx-auto leading-relaxed">
                  {searchQuery 
                    ? 'Tidak ada siswa perwalian yang cocok dengan pencarian Anda.' 
                    : 'Anda belum mendaftarkan siswa perwalian. Silakan gunakan menu "Pilih dari Data Siswa" atau "Tambah Siswa Mandiri" untuk mengisi rombel Anda.'}
                </p>
                {!searchQuery && (
                  <div className="flex justify-center gap-3 mt-6">
                    <button 
                      onClick={() => setTabMode('add_existing')} 
                      className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black uppercase tracking-widest rounded-xl shadow-lg shadow-indigo-500/20"
                    >
                      Pilih Siswa Sekolah
                    </button>
                    <button 
                      onClick={() => setTabMode('add_manual')} 
                      className="px-5 py-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-black uppercase tracking-widest rounded-xl"
                    >
                      Tambah Manual
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {/* MOBILE VIEW CARDS (Visible on sm:hidden) */}
                <div className="block sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
                  {filteredMyWaliRombel.map((s, index) => (
                    <div key={`mob-wr-${s.id}`} className="p-4 bg-white dark:bg-slate-800/80 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="w-6 h-6 rounded-lg bg-indigo-100 dark:bg-indigo-950 text-indigo-800 dark:text-indigo-300 font-black text-xs flex items-center justify-center shrink-0">
                            {index + 1}
                          </span>
                          <div className="min-w-0">
                            <h4 className="font-bold text-sm text-slate-900 dark:text-white truncate">
                              {s.name}
                            </h4>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-2">
                              <span>NIS: {s.nis || '-'} {s.nisn ? `(NISN: ${s.nisn})` : ''}</span>
                            </p>
                          </div>
                        </div>

                        <span className="px-2 py-0.5 rounded-full text-[10px] font-black shrink-0 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400">
                          {s.class || '-'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 dark:bg-slate-900/60 p-2.5 rounded-xl border border-slate-100 dark:border-slate-800">
                        <div>
                          <span className="text-[10px] text-slate-400 block font-bold">Gender & Status</span>
                          <span className="font-semibold text-slate-700 dark:text-slate-300 block">
                            {s.gender} {s.studentId ? '• DB' : '• Mandiri'}
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-400 block font-bold">Jabatan / Catatan</span>
                          <span className="font-semibold text-emerald-600 dark:text-emerald-400 truncate block">
                            {s.notes || '-'}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-1 border-t border-slate-100 dark:border-slate-800">
                        <button
                          onClick={() => setTabMode('bimbingan')}
                          className="px-3 py-1.5 bg-purple-50 dark:bg-purple-950/50 hover:bg-purple-100 text-purple-700 dark:text-purple-300 text-xs font-bold rounded-lg transition-all flex items-center gap-1 cursor-pointer"
                          title="Buka Catatan Bimbingan"
                        >
                          <HeartHandshake size={13} />
                          <span>Bimbingan</span>
                        </button>
                        <button
                          onClick={() => setEditingStudent(s)}
                          className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 text-indigo-700 dark:text-indigo-300 text-xs font-bold rounded-lg transition-all flex items-center gap-1 cursor-pointer"
                        >
                          <Edit3 size={13} />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => handleDeleteStudent(s.id)}
                          className="px-3 py-1.5 bg-rose-50 dark:bg-rose-950/50 hover:bg-rose-100 text-rose-600 dark:text-rose-400 text-xs font-bold rounded-lg transition-all flex items-center gap-1 cursor-pointer"
                        >
                          <Trash2 size={13} />
                          <span>Hapus</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* DESKTOP VIEW TABLE (Visible on hidden sm:block) */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-slate-100 dark:border-slate-800">
                        <th className="pb-3 text-[10px] font-black text-slate-400 uppercase tracking-widest w-12 text-center">No</th>
                        <th className="pb-3 pl-4">
                          {renderSortHeader('Siswa (Nama)', 'name')}
                        </th>
                        <th className="pb-3">
                          {renderSortHeader('NIS / NISN', 'nis')}
                        </th>
                        <th className="pb-3 text-center w-28">
                          {renderSortHeader('Kelas', 'class', 'center')}
                        </th>
                        <th className="pb-3">
                          {renderSortHeader('Gender', 'gender')}
                        </th>
                        <th className="pb-3 pl-4">
                          {renderSortHeader('Jabatan / Catatan', 'notes')}
                        </th>
                        <th className="pb-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right pr-4 w-28">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMyWaliRombel.map((s, index) => (
                        <tr 
                          key={s.id} 
                          className="border-b border-slate-100 dark:border-slate-800/40 hover:bg-slate-50/50 dark:hover:bg-slate-800/10 transition-colors group"
                        >
                          <td className="py-4 text-center text-sm font-bold text-slate-400">{index + 1}</td>
                          <td className="py-4 pl-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-black text-xs shadow-inner ${
                                s.gender === 'Laki-laki' 
                                  ? 'bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400' 
                                  : 'bg-pink-50 text-pink-600 dark:bg-pink-950/40 dark:text-pink-400'
                              }`}>
                                {s.name.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="text-sm font-black text-slate-800 dark:text-white">{s.name}</p>
                                {s.studentId && (
                                  <span className="inline-flex items-center px-2 py-0.5 mt-0.5 bg-slate-100 dark:bg-slate-800 text-slate-400 font-bold rounded text-[9px] uppercase tracking-widest">Database</span>
                                )}
                                {!s.studentId && (
                                  <span className="inline-flex items-center px-2 py-0.5 mt-0.5 bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 font-bold rounded text-[9px] uppercase tracking-widest">Mandiri</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-4 text-sm font-bold text-slate-600 dark:text-slate-400">
                            <div className="flex flex-col">
                              <span>NIS: {s.nis || '-'}</span>
                              {s.nisn && <span className="text-[10px] text-slate-400">NISN: {s.nisn}</span>}
                            </div>
                          </td>
                          <td className="py-4 text-center">
                            <span className="inline-block px-3 py-1 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 font-black rounded-xl text-xs">
                              {s.class || '-'}
                            </span>
                          </td>
                          <td className="py-4 text-sm font-semibold text-slate-600 dark:text-slate-400">
                            {s.gender}
                          </td>
                          <td className="py-4 pl-4 text-sm font-medium text-slate-500 dark:text-slate-400 max-w-[200px] truncate">
                            {s.notes ? (
                              <span className="px-2.5 py-1 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 rounded-xl font-bold text-xs">
                                {s.notes}
                              </span>
                            ) : (
                              <span className="text-slate-300 dark:text-slate-700">-</span>
                            )}
                          </td>
                          <td className="py-4 text-right pr-4">
                            <div className="flex items-center justify-end gap-1 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => setTabMode('bimbingan')}
                                title="Catat Bimbingan Siswa Ini"
                                className="p-2 bg-slate-100 hover:bg-purple-50 dark:bg-slate-800 dark:hover:bg-purple-950/40 text-slate-500 hover:text-purple-600 dark:text-slate-400 dark:hover:text-purple-400 rounded-xl transition-colors"
                              >
                                <HeartHandshake size={14} />
                              </button>
                              <button
                                onClick={() => setEditingStudent(s)}
                                title="Edit Siswa"
                                className="p-2 bg-slate-100 hover:bg-indigo-50 dark:bg-slate-800 dark:hover:bg-indigo-950/40 text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400 rounded-xl transition-colors"
                              >
                                <Edit3 size={14} />
                              </button>
                              <button
                                onClick={() => handleDeleteStudent(s.id)}
                                title="Hapus Siswa"
                                className="p-2 bg-slate-100 hover:bg-rose-50 dark:bg-slate-800 dark:hover:bg-rose-950/40 text-slate-500 hover:text-rose-600 dark:text-slate-400 dark:hover:text-rose-400 rounded-xl transition-colors"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 2: ADD FROM EXISTING SCHOOL STUDENTS */}
        {tabMode === 'add_existing' && (
          <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 p-6 shadow-sm overflow-hidden">
            <div className="mb-6 bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-100/50 dark:border-indigo-900/20 rounded-2xl p-4 flex gap-3 items-start">
              <AlertCircle size={18} className="text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5" />
              <div className="text-xs text-indigo-700 dark:text-indigo-300 leading-relaxed font-medium">
                Pilih siswa dari database induk sekolah di bawah ini untuk didaftarkan ke Rombongan Belajar (Wali Rombel) Anda. Siswa yang sudah terdaftar tidak akan muncul kembali di daftar pilihan ini.
              </div>
            </div>

            {/* Filter and Search Bar */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="relative">
                <Filter size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <select
                  value={selectedClassFilter}
                  onChange={(e) => {
                    setSelectedClassFilter(e.target.value);
                    setSelectedStudentIds(new Set());
                  }}
                  className="w-full pl-11 pr-4 py-3 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-black text-xs sm:text-sm text-slate-800 dark:text-slate-100 transition-all appearance-none cursor-pointer"
                >
                  <option value="SEMUA">SEMUA KELAS</option>
                  {classes.map(c => (
                    <option key={c} value={c}>KELAS {c}</option>
                  ))}
                </select>
              </div>

              <div className="relative sm:col-span-2">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Cari nama atau NIS siswa database..."
                  value={existingSearchQuery}
                  onChange={(e) => setExistingSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-bold text-sm text-slate-800 dark:text-slate-100 transition-all focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Multiple Selection Action */}
            <div className="flex items-center justify-between border-y border-slate-100 dark:border-slate-800 py-3.5 mb-6">
              <div className="flex items-center gap-3">
                <input 
                  type="checkbox"
                  id="select_all_available"
                  checked={availableSchoolStudents.length > 0 && availableSchoolStudents.every(s => selectedStudentIds.has(s.id))}
                  onChange={toggleSelectAllAvailable}
                  className="w-4.5 h-4.5 rounded border-slate-300 dark:border-slate-700 text-indigo-600 focus:ring-indigo-500"
                />
                <label htmlFor="select_all_available" className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer">
                  Pilih Semua ({availableSchoolStudents.length} siswa)
                </label>
              </div>

              <button
                onClick={handleAddExisting}
                disabled={selectedStudentIds.size === 0}
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-black rounded-2xl text-xs uppercase tracking-widest flex items-center gap-2 transition-all shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:shadow-none"
              >
                <Plus size={14} /> Tambahkan Terpilih ({selectedStudentIds.size})
              </button>
            </div>

            {/* Selection Area List */}
            {availableSchoolStudents.length === 0 ? (
              <div className="text-center py-16 px-4">
                <div className="w-16 h-16 rounded-full bg-slate-50 dark:bg-slate-800/40 flex items-center justify-center mx-auto mb-4 border border-slate-100 dark:border-slate-800">
                  <UserCheck size={28} className="text-slate-400" />
                </div>
                <h3 className="text-lg font-black text-slate-800 dark:text-white mb-1">Semua Siswa Terdaftar</h3>
                <p className="text-xs text-slate-400 font-semibold max-w-md mx-auto leading-relaxed">
                  Tidak ada data siswa sekolah yang tersisa untuk ditambahkan berdasarkan filter saat ini. Semua siswa di kelas ini kemungkinan sudah ditambahkan ke Rombel Anda.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[50vh] overflow-y-auto pr-1 custom-scrollbar">
                {availableSchoolStudents.map((s) => {
                  const isChecked = selectedStudentIds.has(s.id);
                  return (
                    <div 
                      key={s.id}
                      onClick={() => toggleStudentSelection(s.id)}
                      className={`p-4 border rounded-2xl flex items-center justify-between cursor-pointer transition-all ${
                        isChecked 
                          ? 'border-indigo-600 bg-indigo-50/40 dark:bg-indigo-950/20 shadow-sm' 
                          : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 hover:bg-slate-50/50 dark:hover:bg-slate-800/20'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input 
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {}} // handled by div click
                          className="w-4.5 h-4.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 pointer-events-none"
                        />
                        <div>
                          <p className="text-sm font-black text-slate-800 dark:text-white">{s.name}</p>
                          <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                            <span>NIS: {s.nis}</span>
                            <span>•</span>
                            <span className="text-indigo-600 dark:text-indigo-400">Kelas {s.class}</span>
                          </div>
                        </div>
                      </div>

                      <div className={`px-2.5 py-1 text-[10px] font-black rounded-lg ${
                        s.gender === 'Laki-laki' 
                          ? 'bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400' 
                          : 'bg-pink-50 text-pink-600 dark:bg-pink-950/40 dark:text-pink-400'
                      }`}>
                        {s.gender === 'Laki-laki' ? 'L' : 'P'}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB 3: ADD NEW STUDENT MANUALLY */}
        {tabMode === 'add_manual' && (
          <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 p-6 sm:p-8 shadow-sm">
            <div className="max-w-2xl">
              <h2 className="text-lg font-black text-slate-800 dark:text-white mb-1">Tambah Siswa Rombel Mandiri</h2>
              <p className="text-xs text-slate-400 font-bold mb-6">Formulir pendaftaran siswa rombel secara manual.</p>

              <form onSubmit={handleAddManual} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2 pl-1">Nama Lengkap Siswa</label>
                    <input
                      type="text"
                      required
                      placeholder="Masukkan nama lengkap siswa..."
                      value={manualName}
                      onChange={(e) => setManualName(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-bold text-sm text-slate-800 dark:text-slate-100 transition-all focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2 pl-1">Jenis Kelamin</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setManualGender('Laki-laki')}
                        className={`flex-1 py-3 text-xs font-black uppercase tracking-wider rounded-2xl border transition-all ${
                          manualGender === 'Laki-laki'
                            ? 'border-blue-500 bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400 font-black'
                            : 'border-slate-100 dark:border-slate-800 text-slate-400 bg-slate-50/50 dark:bg-slate-800/40 hover:text-slate-600 dark:hover:text-slate-200'
                        }`}
                      >
                        Laki-laki (Putra)
                      </button>
                      <button
                        type="button"
                        onClick={() => setManualGender('Perempuan')}
                        className={`flex-1 py-3 text-xs font-black uppercase tracking-wider rounded-2xl border transition-all ${
                          manualGender === 'Perempuan'
                            ? 'border-pink-500 bg-pink-50 text-pink-600 dark:bg-pink-950/40 dark:text-pink-400 font-black'
                            : 'border-slate-100 dark:border-slate-800 text-slate-400 bg-slate-50/50 dark:bg-slate-800/40 hover:text-slate-600 dark:hover:text-slate-200'
                        }`}
                      >
                        Perempuan (Putri)
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2 pl-1">Kelas Rombel</label>
                    <select
                      value={manualClass}
                      onChange={(e) => setManualClass(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-bold text-sm text-slate-800 dark:text-slate-100 transition-all"
                    >
                      {classes.map(c => (
                        <option key={c} value={c}>Kelas {c}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2 pl-1">NIS (Nomor Induk Siswa) - Opsional</label>
                    <input
                      type="text"
                      placeholder="Masukkan NIS..."
                      value={manualNis}
                      onChange={(e) => setManualNis(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-bold text-sm text-slate-800 dark:text-slate-100 transition-all focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2 pl-1">NISN (Opsional)</label>
                    <input
                      type="text"
                      placeholder="Masukkan NISN..."
                      value={manualNisn}
                      onChange={(e) => setManualNisn(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-bold text-sm text-slate-800 dark:text-slate-100 transition-all focus:border-indigo-500"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2 pl-1">Jabatan Kelas / Catatan Khusus</label>
                    <input
                      type="text"
                      placeholder="Misal: KM, Sekretaris, Bendahara, atau catatan medis khusus..."
                      value={manualNotes}
                      onChange={(e) => setManualNotes(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-bold text-sm text-slate-800 dark:text-slate-100 transition-all focus:border-indigo-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setTabMode('list')}
                    className="px-6 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-black rounded-2xl text-xs uppercase tracking-widest"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-black rounded-2xl shadow-xl shadow-indigo-500/20 text-xs uppercase tracking-widest flex items-center gap-2"
                  >
                    <Plus size={14} /> Daftarkan Siswa
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* EDIT MODAL DIALOG */}
      {editingStudent && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-[2px]" onClick={() => setEditingStudent(null)}></div>
          <div className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-[2rem] p-6 sm:p-8 relative shadow-2xl border border-slate-100 dark:border-slate-800 animate-fade-in-up">
            <button 
              onClick={() => setEditingStudent(null)} 
              className="absolute top-6 right-6 p-1 bg-slate-100 dark:bg-slate-800 hover:bg-rose-50 dark:hover:bg-rose-950/40 text-slate-400 hover:text-rose-600 rounded-full transition-colors"
            >
              <X size={18} />
            </button>
            
            <h2 className="text-xl font-black text-slate-800 dark:text-white leading-tight mb-1">Edit Siswa Rombel</h2>
            <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mb-6">Ubah data perwalian siswa</p>

            <form onSubmit={handleUpdateStudent} className="space-y-4">
              <div>
                <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2 pl-1">Nama Lengkap</label>
                <input
                  type="text"
                  required
                  value={editingStudent.name}
                  onChange={(e) => setEditingStudent({ ...editingStudent, name: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-bold text-sm text-slate-800 dark:text-slate-100 transition-all focus:border-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2 pl-1">Jenis Kelamin</label>
                  <select
                    value={editingStudent.gender}
                    onChange={(e) => setEditingStudent({ ...editingStudent, gender: e.target.value as any })}
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-bold text-sm text-slate-800 dark:text-slate-100 transition-all"
                  >
                    <option value="Laki-laki">Laki-laki</option>
                    <option value="Perempuan">Perempuan</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2 pl-1">Kelas</label>
                  <select
                    value={editingStudent.class || ''}
                    onChange={(e) => setEditingStudent({ ...editingStudent, class: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-bold text-sm text-slate-800 dark:text-slate-100 transition-all"
                  >
                    {classes.map(c => (
                      <option key={c} value={c}>Kelas {c}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2 pl-1">NIS</label>
                  <input
                    type="text"
                    value={editingStudent.nis || ''}
                    onChange={(e) => setEditingStudent({ ...editingStudent, nis: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-bold text-sm text-slate-800 dark:text-slate-100 transition-all focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2 pl-1">NISN</label>
                  <input
                    type="text"
                    value={editingStudent.nisn || ''}
                    onChange={(e) => setEditingStudent({ ...editingStudent, nisn: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-bold text-sm text-slate-800 dark:text-slate-100 transition-all focus:border-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2 pl-1">Jabatan / Catatan</label>
                <input
                  type="text"
                  placeholder="KM, Sekretaris, Bendahara, dll"
                  value={editingStudent.notes || ''}
                  onChange={(e) => setEditingStudent({ ...editingStudent, notes: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl outline-none font-bold text-sm text-slate-800 dark:text-slate-100 transition-all focus:border-indigo-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingStudent(null)}
                  className="px-5 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-black rounded-2xl text-xs uppercase tracking-widest"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-black rounded-2xl shadow-xl shadow-indigo-500/20 text-xs uppercase tracking-widest flex items-center gap-2"
                >
                  <Check size={14} /> Simpan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WaliRombelView;
